import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/vue-query';
import apiClient from '@/services/api';

import { formatQueryParams } from '@/composables/api/helpers';
import { queryKeys } from '@/config/vueQuery';
import {
  APP_USERS_PATH,
  USERS_PATH,
  DRIVER_UPDATE_PATH,
  DRIVERS_PATH,
  USER_PATH,
  USER_PERMISSIONS_PATH,
  USERS_PATH_ACTIVATE,
  USERS_PATH_DEACTIVATE,
  OLD_USERS_PATH,
  EXPORT_BO_USERS,
  DOWNLOAD_BO_USERS,
  DOWNLOAD_APP_USERS
} from './constants';
import { getUrlWithParams } from '@/composables/api/helpers';

import type { UserPageResponse, UserPayload } from './types';

export const useUsersApi = () => {
  const queryClient = useQueryClient();

  const refreshUsers = async () => {
    // We only invalidate the query without triggering a refetch immediately.
    // This marks the cache as stale so the next time the component needs data,
    // or if the user navigates, it will fetch fresh data.
    return queryClient.invalidateQueries({
      queryKey: queryKeys.users.all,
      exact: false,
      refetchType: 'none'
    });
  };

  const useUsersList = <TData = UserPageResponse>(
    payload: MaybeRefOrGetter<UserPayload>
  ) => {
    return useQuery<UserPageResponse, Error, TData>({
      queryKey: computed(() => queryKeys.users.filteredLists(toValue(payload))),

      queryFn: async ({ queryKey, signal }): Promise<UserPageResponse> => {
        const p = queryKey[queryKey.length - 1] as UserPayload;
        const roleIdsCsv = Array.isArray(p.roleIds)
          ? p.roleIds.join(',')
          : p.roleIds;
        const params = formatQueryParams({
          ...p,
          roleIds: roleIdsCsv || undefined
        });

        const { data } = await apiClient.get<UserPageResponse>(USERS_PATH, {
          params,
          signal
        });
        return data;
      },

      placeholderData: keepPreviousData
    });
  };

  const deleteUserMutation = useMutation({
    mutationFn: (payload: any) =>
      apiClient.delete(USERS_PATH, { data: payload }),
    onMutate: async (payload) => {
      const deletedIds = Array.isArray(payload) ? payload : [payload];

      // Cancel any outgoing refetches so they don't overwrite our manual update
      await queryClient.cancelQueries({ queryKey: queryKeys.users.lists() });

      // Snapshot the current cache
      const previousData = queryClient.getQueriesData({
        queryKey: queryKeys.users.lists()
      });

      // Optimistically update ALL matching user list queries
      queryClient.setQueriesData(
        { queryKey: queryKeys.users.lists() },
        (oldData: any) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            content: oldData.content.filter(
              (user: any) => !deletedIds.includes(user.id)
            ),
            totalElements: Math.max(
              0,
              (oldData.totalElements || 0) - deletedIds.length
            )
          };
        }
      );

      return { previousData };
    },
    onError: (_err, _variables, context) => {
      // Rollback if the server rejects the delete
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      // We trigger the refresh, but because of the DB delay,
      // we don't 'await' it or block the UI.
      refreshUsers();
    }
  });

  const loadUsers = async (payload) => {
    const params = formatQueryParams(payload);
    const { data } = await apiClient.get(USERS_PATH, { params });
    return data;
  };

  const loadDrivers = async (payload) => {
    const params = formatQueryParams(payload);
    const { data } = await apiClient.get(APP_USERS_PATH, { params });
    return data;
  };

  const addDriver = async (payload) => {
    const { driverType } = payload;

    let path = driverType ? DRIVERS_PATH : OLD_USERS_PATH;

    if (driverType) path = getUrlWithParams(path, { driverType });
    const { data } = await apiClient.post(path, payload);
    return data;
  };

  const addNewUser = async (payload) => apiClient.post(USERS_PATH, payload);

  const addNewUserMutation = useMutation({
    mutationFn: addNewUser,
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.users.lists() });
      const previousData = queryClient.getQueriesData({
        queryKey: queryKeys.users.lists()
      });

      return { previousData };
    },
    onSuccess: (response) => {
      const newUser = response.data;
      queryClient.setQueriesData(
        { queryKey: queryKeys.users.lists() },
        (oldData: any) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            content: [newUser, ...oldData.content],
            totalElements: (oldData.totalElements || 0) + 1
          };
        }
      );
    },
    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      refreshUsers();
    }
  });

  const editUser = async (payload) => {
    const { driverType, id } = payload;

    let url = getUrlWithParams(USER_PATH, { hashId: id });

    if (driverType) {
      url = getUrlWithParams(DRIVER_UPDATE_PATH, { hashId: id, driverType });
    }

    return apiClient.put(url, payload);
  };

  const editUserMutation = useMutation({
    mutationFn: editUser,

    onMutate: async (updatedUser) => {
      // We will mutate the cached data directly using the response from the edit form,
      // without waiting for the server to confirm.
      await queryClient.cancelQueries({ queryKey: queryKeys.users.all });
      const previousData = queryClient.getQueriesData({
        queryKey: queryKeys.users.lists()
      });

      queryClient.setQueriesData(
        { queryKey: queryKeys.users.lists(), exact: false },
        (oldData: any) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            content: oldData.content.map((user: any) =>
              user.id === updatedUser.id ? { ...user, ...updatedUser } : user
            )
          };
        }
      );
      return { previousData };
    },

    onSuccess: (response) => {
      // Your API returns the updated user in response.data
      // We will then inject it to the cached version to ensure the visual data is up to date
      const serverConfirmedUser = response.data;

      queryClient.setQueriesData(
        { queryKey: queryKeys.users.lists(), exact: false },
        (oldData: any) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            content: oldData.content.map((user: any) =>
              user.id === serverConfirmedUser.id ? serverConfirmedUser : user
            )
          };
        }
      );
    },

    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },

    onSettled: async () => {
      // In the background, we will invalidate the cache, so any old instance of that user will be nuked,
      // and a new call to the API will be issued to get the latest data
      refreshUsers();
    }
  });

  const loadSingleUser = async ({ hashId, path = undefined }) => {
    const config = path ? { params: { path } } : {};
    const url = getUrlWithParams(USER_PATH, { hashId });
    const { data } = await apiClient.get(url, config);
    return data;
  };

  const deleteUsers = async (payload) =>
    apiClient.delete(USERS_PATH, { data: payload });

  const activateUsers = async ({ users, action }) => {
    const path = action ? USERS_PATH_ACTIVATE : USERS_PATH_DEACTIVATE;
    const usersIds = users.map((user) => user.id);
    return apiClient.put(path, usersIds);
  };

  const updateUserPermissionsMutation = useMutation({
    mutationFn: async (payload: any) => {
      const { id, permissions } = payload;
      const url = getUrlWithParams(USER_PERMISSIONS_PATH, { hashId: id });
      return await apiClient.put(url, permissions);
    },

    onMutate: async (payload) => {
      // Stop background noise
      await queryClient.cancelQueries({ queryKey: queryKeys.users.all });

      const previousData = queryClient.getQueriesData({
        queryKey: queryKeys.users.lists()
      });

      // Mask the UI: Show it as empty so old data doesn't "flicker"
      queryClient.setQueriesData(
        { queryKey: queryKeys.users.lists(), exact: false },
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            content: old.content.map((u: any) =>
              u.id === payload.id
                ? {
                    ...u,
                    isUpdatingPermissions: true,
                    permissions: [],
                    permissionsV2: []
                  }
                : u
            )
          };
        }
      );

      return { previousData };
    },

    onSuccess: async () => {
      await new Promise((r) => setTimeout(r, 4000));

      // Reset Queries will mark the cache as stale and trigger refetches for any active queries with this key.
      await queryClient.resetQueries({
        queryKey: queryKeys.users.all,
        exact: false
      });

      // If any "ghost" filters are still lingering, this wipes them out of the RAM entirely.
      queryClient.removeQueries({
        queryKey: queryKeys.users.all,
        exact: false,
        type: 'inactive'
      });
    },

    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        context.previousData.forEach(([key, data]) =>
          queryClient.setQueryData(key, data)
        );
      }
    }
  });

  const exportBackofficeUsers = async () => {
    const { data } = await apiClient.get(EXPORT_BO_USERS);
    return data;
  };

  const downloadBackofficeUsers = async (payload) => {
    const params = formatQueryParams(payload);
    const { data } = await apiClient.get(DOWNLOAD_BO_USERS, {
      params
    });
    return data;
  };

  const downloadAppUsers = async (payload) => {
    const params = formatQueryParams(payload);
    const { data } = await apiClient.get(DOWNLOAD_APP_USERS, {
      params
    });
    return data;
  };

  const isFetchingUsers = computed(() => {
    return (
      deleteUserMutation.isPending.value ||
      editUserMutation.isPending.value ||
      addNewUserMutation.isPending.value ||
      updateUserPermissionsMutation.isPending.value
    );
  });

  return {
    useUsersList,
    deleteUserMutation,
    editUserMutation,
    addNewUserMutation,
    isFetchingUsers,
    loadUsers,
    loadDrivers,
    addDriver,
    addNewUser: addNewUserMutation.mutateAsync,
    editUser: editUserMutation.mutateAsync,
    loadSingleUser,
    deleteUsers,
    activateUsers,
    updateUserPermissions: updateUserPermissionsMutation.mutateAsync,
    exportBackofficeUsers,
    downloadBackofficeUsers,
    downloadAppUsers
  };
};
